import React from 'react';
import { useTranslation } from 'react-i18next';
import { reportError, addBreadcrumb } from '@/utils/sentry';
import * as Sentry from '@sentry/react';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  H1,
  H3,
  H4,
  P,
  Small,
  InlineCode,
  MultilineCode,
} from '@/components/ui/typography';

interface ErrorPageProps {
  statusCode: 400 | 404 | 500;
  title?: string;
  description?: string;
  showHomeButton?: boolean;
  showRetryButton?: boolean;
  error?: Error;
}

const ERROR_COLORS = {
  400: 'orange',
  404: 'blue',
  500: 'red',
} as const;

export const ErrorPage: React.FC<ErrorPageProps> = ({
  statusCode,
  title,
  description,
  showHomeButton = true,
  showRetryButton = false,
  error,
}) => {
  const { t } = useTranslation();

  const errorConfig = {
    title: title || t(`errorPage.${statusCode}.title`),
    description: description || t(`errorPage.${statusCode}.description`),
    icon: t(`errorPage.${statusCode}.icon`),
  };

  React.useEffect(() => {
    // Report error to Sentry if it's a server error or if an error object is provided
    if (statusCode === 500 || error) {
      const errorObj =
        error || new Error(`HTTP ${statusCode}: ${errorConfig.title}`);

      addBreadcrumb(`Error page displayed: ${statusCode}`, 'error', 'error');

      reportError(errorObj, {
        statusCode,
        errorPage: true,
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    } else {
      // For non-error cases (like 404), just add a breadcrumb
      addBreadcrumb(
        `Error page displayed: ${statusCode}`,
        'navigation',
        'info'
      );
    }
  }, [statusCode, error, errorConfig.title]);

  const handleRetry = () => {
    addBreadcrumb('User clicked retry on error page', 'user', 'info');
    window.location.reload();
  };

  const handleGoHome = () => {
    addBreadcrumb('User navigated home from error page', 'user', 'info');
    window.location.href = '/';
  };

  const getColorVariant = (color: string) => {
    switch (color) {
      case 'orange':
        return 'text-orange-600';
      case 'blue':
        return 'text-blue-600';
      case 'red':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const color = ERROR_COLORS[statusCode];
  const colorClass = getColorVariant(color);

  return (
    <PageContainer>
      <div className='flex items-center justify-center min-h-[60vh]'>
        <Card glassmorphism className='w-full max-w-lg mx-auto text-center'>
          <CardHeader>
            {/* Icon and Status Code */}
            <div className='flex flex-col items-center space-y-4'>
              <div className='text-6xl'>{errorConfig.icon}</div>
              <H1 className={`text-8xl font-bold ${colorClass}`}>
                {statusCode}
              </H1>
            </div>
          </CardHeader>

          <CardContent className='space-y-6'>
            {/* Title */}
            <H3 className='text-gray-900'>{errorConfig.title}</H3>

            {/* Description */}
            <P className='text-gray-600 text-base'>{errorConfig.description}</P>

            {/* Error reported message */}
            <Small className='text-gray-500'>
              {t('errorPage.common.errorReported')}
            </Small>

            {/* Development Info */}
            {import.meta.env.MODE === 'development' && error && (
              <Card className='text-left bg-yellow-50 border-yellow-200'>
                <CardContent className='pt-4'>
                  <H4 className='text-yellow-800 mb-2'>
                    {t('errorPage.common.developmentDetails')}
                  </H4>
                  <InlineCode className='bg-yellow-100 text-yellow-800 block mb-3 p-2 text-xs break-all'>
                    {error.message}
                  </InlineCode>
                  {error.stack && (
                    <details className='mt-2'>
                      <summary className='text-yellow-700 cursor-pointer text-sm mb-2'>
                        {t('errorPage.common.stackTrace')}
                      </summary>
                      <MultilineCode className='bg-yellow-100 text-yellow-700 text-xs max-h-32 overflow-auto'>
                        {error.stack}
                      </MultilineCode>
                    </details>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Event ID */}
            {(statusCode === 500 || error) && (
              <Card className='bg-blue-50 border-blue-200'>
                <CardContent className='pt-4'>
                  <Small className='text-blue-700 block'>
                    <strong>{t('errorPage.common.errorId')}:</strong>{' '}
                    {Sentry.lastEventId() || 'N/A'}
                  </Small>
                  <Small className='text-blue-600 mt-1 block'>
                    {t('errorPage.common.errorIdDescription')}
                  </Small>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            {(showRetryButton || showHomeButton) && (
              <div className='flex flex-col sm:flex-row gap-3 pt-2'>
                {showRetryButton && (
                  <Button
                    onClick={handleRetry}
                    className={`flex-1 bg-${color}-600 hover:bg-${color}-700`}
                  >
                    {t('errorPage.common.tryAgain')}
                  </Button>
                )}
                {showHomeButton && (
                  <Button
                    onClick={handleGoHome}
                    variant='outline'
                    className='flex-1'
                  >
                    {t('errorPage.common.goHome')}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};
